import { useTranslation } from 'react-i18next';
import type { Flight } from '@flugwettrennen/shared';
import { hhmm } from '../lib/format';

/**
 * `expand` = es sind noch geladene Flüge verborgen (kostet nichts),
 * `load` = die Tafel ist zu Ende, weitere Stunden kosten eine Anfrage beim
 * Datenanbieter, `null` = nichts mehr nachzuladen.
 */
export type MoreMode = 'expand' | 'load' | null;

interface Props {
  airportName: string;
  flights: Flight[];
  rolledIds: string[];
  onSelect: (flightId: string) => void;
  onDetails: (flight: Flight) => void;
  busyId: string | null;
  disabled?: boolean;
  /** Wie viele der geladenen Flüge angezeigt werden. */
  visibleCount: number;
  moreMode: MoreMode;
  moreBusy?: boolean;
  onMore: () => void;
}

export function DepartureBoard({
  airportName,
  flights,
  rolledIds,
  onSelect,
  onDetails,
  busyId,
  disabled,
  visibleCount,
  moreMode,
  moreBusy,
  onMore,
}: Props) {
  const { t } = useTranslation();
  const shown = flights.slice(0, visibleCount);

  return (
    <div className="board" aria-label={t('game.board')}>
      <div className="board-head">
        <span className="dot" />
        <span>{t('game.board')}</span>
        <span className="spacer" style={{ flex: 1 }} />
        <span style={{ opacity: 0.7 }}>{airportName}</span>
      </div>
      <div className="board-rows" role="list">
        {shown.map((f) => {
          const revised = f.estimatedDeparture && f.estimatedDeparture !== f.scheduledDeparture;
          const rolled = rolledIds.includes(f.id);
          // Ein bereits gewürfelter, wählbarer Flug im Boarding ist verpasst/weg.
          const missed = f.selectable && rolled;
          const cat = f.category !== 'passenger' ? t(`game.category.${f.category}`) : null;
          return (
            <div
              key={f.id}
              role="listitem"
              className={`flap ${!f.selectable ? 'blocked' : ''} ${missed ? 'missed' : ''}`}
              data-testid="flight-row"
              data-selectable={f.selectable}
              data-missed={missed}
            >
              <div className="time">
                {hhmm(f.scheduledDeparture)}
                {revised && <span className="revised">{hhmm(f.estimatedDeparture)}</span>}
              </div>
              <button
                type="button"
                className="dest-btn"
                onClick={() => onDetails(f)}
                title={t('game.details')}
                data-testid="flight-details-btn"
              >
                <div className="dest">
                  <div className="code">
                    {f.destination.iata} · {f.destination.name}
                  </div>
                  <div className="name">
                    {f.destination.countryName || f.destination.country} · {t('game.arrival')} {hhmm(f.scheduledArrival)}
                  </div>
                </div>
              </button>
              <div className="meta">
                <span className="meta-line">
                  {f.flightNumber} · {f.airlineName}
                </span>
                <span className="meta-line">
                  {f.aircraftType ?? '—'}
                  {typeof f.delayMinutes === 'number' && f.delayMinutes > 0 && (
                    <span className="pill pill-delay" style={{ padding: '1px 6px' }}>
                      {t('game.delay', { count: f.delayMinutes })}
                    </span>
                  )}
                </span>
              </div>
              <div className="action">
                {missed ? (
                  <span className="pill pill-missed" data-testid="flight-missed">✕ {t('game.missed')}</span>
                ) : f.selectable ? (
                  <button
                    className="board-flap-btn"
                    disabled={disabled || busyId === f.id}
                    onClick={() => onSelect(f.id)}
                    data-testid="select-flight"
                  >
                    {busyId === f.id ? t('game.rolling') : t('game.select')}
                  </button>
                ) : (
                  <span className="pill pill-stop">
                    {cat ?? t(`game.reason.${f.notSelectableReason ?? 'notCommercial'}`)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {moreMode && (
        <div className="board-foot">
          <button
            type="button"
            className="board-flap-btn board-more"
            data-testid="board-more"
            data-mode={moreMode}
            disabled={moreBusy}
            onClick={onMore}
          >
            {moreBusy
              ? t('game.more.loading')
              : moreMode === 'expand'
                ? t('game.more.expand')
                : t('game.more.load')}
          </button>
          <span className="board-foot-note">
            {moreMode === 'load'
              ? t('game.more.costHint')
              : t('game.more.shown', { shown: shown.length, total: flights.length })}
          </span>
        </div>
      )}
    </div>
  );
}
